// shared/components/SelectInput.tsx
import { useField, useFormikContext } from 'formik'
import React from 'react'

function fieldErrorMessage(error: unknown): string | undefined {
  if (error == null || error === false) return undefined
  if (typeof error === 'string') return error
  if (Array.isArray(error)) {
    const parts = error
      .map((e) => fieldErrorMessage(e))
      .filter((s): s is string => Boolean(s))
    return parts.length ? parts.join(', ') : undefined
  }
  if (typeof error === 'object') {
    for (const v of Object.values(error as Record<string, unknown>)) {
      const m = fieldErrorMessage(v)
      if (m) return m
    }
  }
  return undefined
}

interface SelectOption {
  value: string | boolean
  label: string
}

interface SelectInputProps {
  name: string
  label?: string
  labelPosition?: 'top' | 'left'
  labelWithBox?: boolean
  options: readonly SelectOption[]
  firstItemLabel?: string
  wrapperClassName?: string
  className?: string
  isMulti?: boolean
  disabled?: boolean
  defaultValue?: string | string[] | boolean
  onChange?: (value: string | string[] | boolean | null) => void
}

const SelectInput: React.FC<SelectInputProps> = ({
  name,
  label,
  labelPosition = 'top',
  labelWithBox = false,
  options,
  firstItemLabel = 'Seçiniz',
  wrapperClassName = '',
  className = 'form-select',
  isMulti = false,
  disabled = false,
  defaultValue,
  onChange,
}) => {
  const [field, meta, helpers] = useField(name)
  const { submitCount } = useFormikContext()

  // Component mount olduğunda default değeri set et
  React.useEffect(() => {
    if (defaultValue !== undefined && !field.value) {
      helpers.setValue(defaultValue)
    }
  }, [])

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    let value: string | string[] | null = null

    if (isMulti) {
      const selected = Array.from(event.target.selectedOptions, (option) => option.value) 
      value = selected.length ? selected : null
      helpers.setValue(value)
    } else {
      value = event.target.value || null
      helpers.setValue(value)
    }

    onChange?.(value)
  }

  const renderLabel = () => {
    if (!label) return null

    return (
      <label className={`form-label fw-bold ${labelPosition === 'left' ? 'mb-0' : ''}`}>
        {label}
      </label>
    )
  }

  const errMsg = fieldErrorMessage(meta.error)
  const showInvalid = (meta.touched || submitCount > 0) && errMsg

  const selectElement = (
    <div className={labelPosition === 'left' && label ? 'flex-grow-1' : 'w-100'}>
      <select
        {...field}
        multiple={isMulti}
        disabled={disabled}
        className={`${className} ${showInvalid ? 'is-invalid' : ''}`}
        onChange={handleChange}
      >
        {!isMulti && <option value=''>{firstItemLabel}</option>}
        {options.map((opt) => (
          <option key={String(opt.value)} value={String(opt.value)}>
            {opt.label}
          </option>
        ))}
      </select>
      {showInvalid && <div className='invalid-feedback d-block'>{errMsg}</div>}
    </div>
  )

    if (label && labelPosition === 'left' && labelWithBox) {
    return (
      <div className={`${wrapperClassName} h-100`}> 
          <div className='input-group'> 
            <span className='input-group-text'>{label}</span>
            {selectElement} 
        </div>
      </div>
    )
  }

  if (labelPosition === 'left' && label) {
    return (
      <div className={`${wrapperClassName} h-100`}>
        <div className='d-flex align-items-start gap-3 h-100'>
          <div className='input-group'>
            <div className='flex-shrink-0 pt-2'>
              {renderLabel()}
            </div>
            {selectElement}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`${wrapperClassName} h-100`}>
      <div className='input-group'>
        {renderLabel()}
        {selectElement}
      </div>
    </div>
  )
}

export default SelectInput